import React, { useEffect, useState } from 'react'
import { InvalidEntry, Invitation, parseAndValidateListOfInvitations } from './bulk_invite_logic'
import { AlertType } from '@amzn/storm-ui/dist/types/Alert/types'
import { UsersInviteDataEntryError, UsersInviteDataEntry } from './users_invite_wizard_steps/users_invite_data_entry'
import { UsersInviteDefects } from './users_invite_wizard_steps/users_invite_defects'
import { UsersInvitePreview } from './users_invite_wizard_steps/users_invite_preview'
import { UsersInviteExecution } from './users_invite_wizard_steps/users_invite_execution'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'

export type ProcessStep = 'DataEntry' | 'Preview' | 'Execution' | 'Defects'
export const BulkUsersInvite: React.FunctionComponent = (): JSX.Element => {
  const [processStep, setProcessStep] = useState<ProcessStep>('DataEntry')
  const [dataEntryError, setDataEntryError] = useState<UsersInviteDataEntryError | undefined>(undefined)
  const [listOfInvitees, setListOfInvitees] = useState<string[]>([])

  const [invalidEntries, setInvalidEntries] = useState<InvalidEntry[]>([])
  const [defectFixupInvitees, setDefectFixupInvitees] = useState<string[]>([])

  const [listOfInvitations, setListOfInvitations] = useState<Invitation[]>([])

  const [previewAlertType, setPreviewAlertType] = useState<AlertType>('info')
  const [previewUpdateCompleted, setPreviewUpdateCompleted] = useState(false)

  const [successfulInviteCount, setSuccessfulInviteCount] = useState<number>(0)

  useEffect(() => {
    getMetricsPublisherChainAsync('AdsConsoleInt').then((p) => p.publishCounter('view', 1).publishString(`page`, `bulk-users-invite`))
  }, [])

  useEffect(() => {
    if (listOfInvitees.length > 0 && previewUpdateCompleted) {
      setPreviewAlertType('error')
      handleDataEntryComplete()
    }
  }, [previewUpdateCompleted])

  const handleDataEntryComplete = (): void => {
    setSuccessfulInviteCount(0)
    const { validInvitations, invalidRows } = parseAndValidateListOfInvitations(listOfInvitees)
    setListOfInvitations(validInvitations)
    setInvalidEntries(invalidRows.Invalid)

    if (invalidRows.TooManyEntries.length > 0) {
      setDataEntryError('TooManyEntries')
      return
    }

    if (invalidRows.Invalid.length > 0) {
      setDefectFixupInvitees(invalidRows.Invalid.map((e) => e.value))
      setProcessStep('Defects')
      return
    }

    if (validInvitations.length === 0) {
      setDataEntryError('EmptyContent')
      return
    }

    setPreviewUpdateCompleted(false)
    setDataEntryError(undefined)
    setProcessStep('Preview')
  }

  const handleDefectCorrection = (): void => {
    const { validInvitations, invalidRows } = parseAndValidateListOfInvitations(defectFixupInvitees)
    setListOfInvitations(listOfInvitations.concat(validInvitations))
    setInvalidEntries(invalidRows.Invalid)

    if (invalidRows.Invalid.length > 0) {
      return
    }

    setProcessStep('Preview')
  }

  const handleContinueWithoutCorrection = (): void => {
    if (listOfInvitations.length === 0) {
      return
    }

    setProcessStep('Preview')
  }

  const handleNewInvitesSent = (): void => {
    setPreviewAlertType('info')
    setSuccessfulInviteCount(listOfInvitations.length)
    setProcessStep('DataEntry')
    setListOfInvitees([])
    setInvalidEntries([])
    setListOfInvitations([])
  }

  const element = (() => {
    switch (processStep) {
      case 'DataEntry':
        return (
          <UsersInviteDataEntry
            listOfInvitees={listOfInvitees}
            setListOfInvitees={setListOfInvitees}
            dataEntryError={dataEntryError}
            nextAction={handleDataEntryComplete}
            successfulInviteCount={successfulInviteCount}
            setSuccessfulInviteCount={setSuccessfulInviteCount}
          />
        )
      case 'Defects':
        return (
          <UsersInviteDefects
            invalidEntries={invalidEntries}
            defectFixupInvitees={defectFixupInvitees}
            setDefectFixupInvitees={setDefectFixupInvitees}
            fixupAction={handleDefectCorrection}
            skipAction={handleContinueWithoutCorrection}
          />
        )
      case 'Preview':
        return <UsersInvitePreview invitations={listOfInvitations} alertType={previewAlertType} confirmAction={() => setProcessStep('Execution')} />
      case 'Execution':
        return (
          <UsersInviteExecution
            invitations={listOfInvitations}
            alertType={previewAlertType}
            onNewInvitesSent={handleNewInvitesSent}
            setErrors={setListOfInvitees}
            setUpdateCompleted={() => setPreviewUpdateCompleted(true)}
          />
        )
    }
  })()

  return element
}
